/* eslint-disable max-len */
// Helper functions for the frontend

import config from "./config.js";

const tricycleImageDomain = process.env.JS_TRICYCLE_IMAGE_DOMAIN;
const visualizerDomain = process.env.JS_FLOORVANAPLUS_VISUALIZER_DOMAIN;

export default {
  // Gets category collection overview page URL
  getCollectionOverviewUrl: function(collectionCode) {
    return `/Product-Collection/${encodeURIComponent(collectionCode)}`;
  },

  getSearchTermUrl: function(searchTerm, tab = '') {
    return `/search?searchTerm=${encodeURIComponent(searchTerm)}` + (tab != '' ? '&tab=' + tab : "");
  },

  // Gets product detail page URL
  getProductDetailUrl: function(styleNameURL, sellingStyleNumber, colorNumber) {
    if (colorNumber) {
      return `/product/detail/${styleNameURL}-${sellingStyleNumber}/${colorNumber}`;
    } else {
      return `/product/detail/${styleNameURL}-${sellingStyleNumber}`;
    }
  },

  // Get 3D source url
  getThreeDVisualizeUrl: function(
    visualizeIn3dURL,
    sellingStyleNumber,
    colorNumber,
    installationMethod,
    UserGUID = ''
  ) {
    return `${visualizeIn3dURL}&sellingStyleNumber=${sellingStyleNumber}&colorNumber=${colorNumber}&installationMethod=${installationMethod}${UserGUID != '' ? '&guid=' + UserGUID : ''}`;
  },

  // Returns product color thumbnail image URL
  getColorThumbnailImageUrl: function(sellingStyleNumber, colorNumber, size = null, productTypeDesc = null) {
    if(size != null && productTypeDesc != null){
      return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/tiles/${size}/1`;
    }
    return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/thumbnail`;
  },

  // Returns product color thumbnail image URL
  getColorInstallationImageUrl: function(
    sellingStyleNumber,
    colorNumber,
    tryciclyCode,
    size
  ) {
    return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/installs/${tryciclyCode}/repeat?size=${
      size ? size : 3300
    }`;
  },

  // Returns room scene thumbnail image URL from Algolia result, when displaying products in a grid
  getRoomSceneThumbnailImageUrlFromAlgoliaResult: function(result) {
    if (result.roomscene) {
      return result.roomscene;
    } else {
      let roomScene = this.getDefaultRoomSceneFromAlgoliaResult(result);
      return roomScene ?? "IMAGE_NOT_FOUND";
    }
  },
  getDefaultRoomSceneFromAlgoliaResult: function(result, size) {
    let trycicleCode = config.search.defaultTrycicleCode;
    const colorNumber =
      result.colors.colorNumber ?? result.colors[0].colorNumber;
    if (
      Array.isArray(result.recommendedInstallationMethodsInfo) &&
      result.recommendedInstallationMethodsInfo.length > 0
    ) {
      trycicleCode = result.recommendedInstallationMethodsInfo[0].tricycleCode;
    }
    return this.getInteractiveRoomSceneImageUrl(
      result.sellingStyleNumber,
      colorNumber,
      1162,
      trycicleCode,
      size ?? 300
    );
  },

  // Returns product color repeat image URL
  getColorRepeatImageUrl: function(sellingStyleNumber, colorNumber, size) {
    return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/repeat/?pixels=${
      size ? size : 1500
    }`;
  },

  // Returns product color top down install image URL
  getColorTopDownInstallImageUrl: function(
    sellingStyleNumber,
    colorNumber,
    installationMethod,
    size
  ) {
    return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/installs/${installationMethod}?physWidth=6ft&physHeight=6ft&pixels=${
      size ? size : 1500
    }`;
  },

  // Returns rugs color top down install image URL
  getRugColorInstallImageUrl: function(
    sellingStyleNumber,
    colorNumber,
    shape,
    sizes
  ) {
    return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/arearug?physWidth=${
      sizes[0]
    }&physHeight=${sizes[1]}&shape=${shape ?? "rectangle"}&pixels=1500`;
  },

  // Gets interactive rug room scene image URL
  getRugRoomSceneImageUrl: function(
    sellingStyleNumber,
    colorNumber,
    roomSceneId,
    shape,
    sizes
  ) {
    return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/arearug/rooms/${roomSceneId}?physWidth=${
      sizes[0]
    }&physHeight=${sizes[1]}&shape=${shape ?? "rectangle"}&pixels=1500`;
  },

  // Gets interactive room scene image URL
  getInteractiveRoomSceneImageUrl: function(
    sellingStyleNumber,
    colorNumber,
    roomSceneId,
    installationMethodId,
    size,
    productType = null,
    inventoryType = null
  ) {
    if (
      (inventoryType === "resilient" && productType === "sheet") ||
      (inventoryType === "carpet" && productType === "broadloom")
    ) {
      return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/rooms/${roomSceneId}?pixels=${
        size ? size : 1500
      }`;
    } else {
      return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/rooms/${roomSceneId}/installs/${installationMethodId}?pixels=${
        size ? size : 1500
      }`;
    }
  }, 

  // Get tile image URL 
  getTileImageUrl: function(sellingStyleNumber, colorNumber, width, length, variation) {
    return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/tiles/${width}x${length}/${variation}?pixels=325`;
  },
  // Gets tileset zip file URL
  getTilesetUrl: function(sellingStyleNumber, colorNumber, width, length) {
    return `${tricycleImageDomain}/designs/${sellingStyleNumber}/colors/${colorNumber}/tileset/${width}x${length}`;
  },

  // Gets user-friendly product refinement name (used on product category page filters)
  getProductSearchRefinementName(attributeName, attributeValue) {
    if (config.search.productFacetNameMappings[attributeName]) {
      return (
        config.search.productFacetNameMappings[attributeName][attributeValue] ??
        attributeValue
      );
    } else {
      return attributeValue;
    }
  },

  getPatcarftVisualizerLink: function(
    collectionName,
    styleNumber,
    colorNumber,
    trycicleCode
  ) {
    return `${visualizerDomain}/visualizer?collection_0=${collectionName}&product_0=${styleNumber}&color_0=${colorNumber}&pattern_0=${trycicleCode}`;
  },

  adjustRefinementsVisibility: function(items) {
    let elementsToHide = document.querySelectorAll(
      "div[data-hide-for-product-type]"
    );

    let inventoryTypeRefinement = items.find(
      i => i.attribute === "inventoryType"
    );

    for (let i = 0; i < elementsToHide.length; i++) {
      const element = elementsToHide[i];
      if (
        inventoryTypeRefinement &&
        inventoryTypeRefinement.refinements.length === 1
      ) {
        let productTypes = element
          .getAttribute("data-hide-for-product-type")
          .split(",");
        if (
          productTypes.findIndex(
            i =>
              i.toLowerCase() ===
              inventoryTypeRefinement.refinements[0].value.toLowerCase()
          ) >= 0
        ) {
          element.classList.add("d-none");
          continue;
        }
      }
      element.classList.remove("d-none");
    }
  },
  toggleInStockDropDown: function(attribute, forceHide) {
    if (attribute !== "colors.isInStock") return;

    let deviceType = window.innerWidth < 1024 ? "mobile" : "desktop";

    let element = document.querySelector(
      `.wf-product-category-sidebar-${deviceType} .custom-select.availability-select`
    );

    let secondDropDown = document.querySelector(
      `.wf-product-category-sidebar-${
        deviceType == "mobile" ? "desktop" : "mobile"
      } .custom-select.availability-select`
    );

    let inStockCheckbox = document.querySelector(
      `.wf-product-category-sidebar-${deviceType} div[filter-item="colors.isInStock"] #in-stock-swicth`
    );
    let resultsSection = document.querySelectorAll(
      ".wf-product-category-results-section"
    );

    if ((inStockCheckbox && !inStockCheckbox.checked) || forceHide) {
      if (element.value != "0") {
        element.value = "0";
        secondDropDown.value = "0";
        element.dispatchEvent(new Event("change"));
      }
      element.classList.add("d-none");
      secondDropDown.classList.add("d-none");
      resultsSection.forEach(el =>
        el.classList.remove("display-in-stock-label")
      );
      return;
    }
    element.classList.remove("d-none");
    secondDropDown.classList.remove("d-none");
    resultsSection.forEach(el => el.classList.add("display-in-stock-label"));
  },
  // Gets category collection overview page URL
  getItemBackgroundImage: function(element, imageType, containerImage) {
    const type = element.inventoryType;
    const styleNumber = element.styleNumber ?? element.sellingStyleNumber;
    let colorNumber =
      element.colorNumber !== undefined
        ? element.colorNumber
        : element.colors?.colorNumber;

    if (type === "Paint") return undefined;

    let assetBaseUrl =
      window.VT_PARAMS && window.VT_PARAMS["ASSET_BASE_URL"]
        ? window.VT_PARAMS["ASSET_BASE_URL"]
        : process.env["VUE_APP_" + "ASSET_BASE_URL"];

    containerImage = containerImage
      ? containerImage
      : `https://img.shawinc.com/s7/is/image/ShawIndustriesCommercial/${styleNumber}_${colorNumber}_MAIN`;

    if (type.toUpperCase() === "POM") {
      containerImage = `${assetBaseUrl}/pom/${imageType}/${
        imageType === "slides" ? "pom_" : ""
      }${styleNumber}_${colorNumber}.${imageType === "slides" ? "jpg" : "png"}`;
      containerImage = containerImage.toLowerCase();
    }

    if (type === "Inspiration") {
      colorNumber =
        colorNumber && colorNumber.length > 0 ? colorNumber : styleNumber;
      containerImage = `${assetBaseUrl}/inspiration/${colorNumber}.jpg`;
      containerImage = containerImage.toLowerCase();
    }

    if (type === "Premix") {
      containerImage = `${assetBaseUrl}/premix/${colorNumber}.png`;
      containerImage = containerImage.toLowerCase();
    }
    return containerImage;
  },

  //Match and highlight with red match in text
  matchText(text, searchTerm) {
    return text?.replace(
      new RegExp(searchTerm, "gi"),
      match => `<span class="danger">${match}</span>`
    );
  },

  // Get widen image URL based on embedeId
  getWidenImgURL(embededId, size = 500) {
    const baseURL = process.env.JS_WIDEN_IMAGES_DOMAIN;
    return `${baseURL}/content/${embededId}/png?width=${size}`;
  },

  //Add .disable-scroll class on body to prevent scrolling in some cases
  disableScrolling() {
    $("html").addClass("disable-scroll");
    $("body").addClass("disable-scroll");
  },

  //Remove .disable-scroll class on body to enable scrolling if disabled
  enableScrolling() {
    $("html").removeClass("disable-scroll");
    $("body").removeClass("disable-scroll");
  }
};

export function imgCoordinateTransformer(imgData,imageCropData) {
 
// const { initialCanvasData: { scale }, cropBoxData: cbox } = imgData;
const {x, y, width, height} = imageCropData;


return {
  offsetWidth: Math.round(x),
  offsetHeight: Math.round(y),
  width: Math.round(width),
  height: Math.round(height),
}


}
 
export function coverImageCroppingsTransformerForProject(imageData, boardId,imageCropData) {
  const thumbnailImage = imgCoordinateTransformer(imageData,imageCropData);

  return [
    {
      ...imgCoordinateTransformer(imageData?.cover, imageCropData?.cover),
      isThumbnail: false,
      boardId: boardId || undefined,
    },
    {
      ...imgCoordinateTransformer(imageData?.thumbnail, imageCropData?.thumbnail),
      isThumbnail: true,
      boardId: boardId || undefined,
    },
  ];
}
export function coverImageCroppingsTransformer(imageData, boardId,imageCropData) {
  const thumbnailImage = imgCoordinateTransformer(imageData,imageCropData);

  return [
    {
      ...thumbnailImage,
      isThumbnail: false,
      boardId: boardId || undefined,
    },
    {
      ...thumbnailImage,
      isThumbnail: true,
      boardId: boardId || undefined,
    },
  ];
}
